import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

const CONTROL_HEIGHT = '36px';
const CONTROL_RADIUS = '8px';
const CONTROL_BORDER = `1px solid ${theme.palette.neutral[200]}`;

export const toolbarActionsSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        flexWrap: 'wrap',
    },
};

/**
 * A select that belongs in a toolbar rather than in a form.
 *
 * The same height, radius and border as the search box it stands next to. A form select carries a
 * floating label above itself, which is right on a form and wrong here: it sits a label's height
 * taller than everything around it, and what it filters by is better said inside it — "All roles"
 * reads as both the label and the value.
 */
// No SxProps annotation, unlike its neighbours: that type is a union that includes arrays, so the
// moment this is spread into a nested selector - which the searchable toolbar select does, to put a
// toolbar's border on an Autocomplete's input - TypeScript has to consider the array case and refuses.
// Inferring the object keeps it assignable to sx everywhere it was already used.
export const toolbarSelectSx = {
    height: CONTROL_HEIGHT,
    background: theme.palette.common.white,
    borderRadius: CONTROL_RADIUS,
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-notchedOutline': {
        border: CONTROL_BORDER,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.neutral[300],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
    },
};

export const searchInputSx: SxProps<Theme> = {
    width: '260px',
    height: CONTROL_HEIGHT,
    background: theme.palette.common.white,
    borderRadius: CONTROL_RADIUS,
    '&.MuiOutlinedInput-root': {
        paddingLeft: '10px',
        '& fieldset': {
            borderColor: theme.palette.neutral[200],
        },
        '&:hover fieldset': {
            borderColor: theme.palette.neutral[300],
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
        },
    },
    '& svg': {
        fill: theme.palette.neutral[400],
        fontSize: '20px',
    },
    '& .MuiInputBase-input': {
        fontSize: '14px',
        padding: '8px',
        paddingLeft: '6px',
        // Safari zooms the page whenever it focuses a field smaller than 16px, and then leaves it
        // scrolled sideways. Every search box in the product was 14px.
        [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
        },
        '&::placeholder': {
            color: theme.palette.neutral[400],
            opacity: 1,
        },
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
};

/**
 * A text button that sits beside the search: Export, and an action on the rows below.
 *
 * White, bordered and as tall as the search box, so the toolbar reads as one row of controls; a
 * filled button there shouted louder than the list it belongs to.
 */
export const toolbarButtonSx: SxProps<Theme> = {
    height: CONTROL_HEIGHT,
    px: 1.5,
    minWidth: 'auto',
    gap: 0.5,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: CONTROL_RADIUS,
    border: CONTROL_BORDER,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.neutral[500],
    boxShadow: 'none',
    '&:hover': {
        borderColor: theme.palette.neutral[300],
        backgroundColor: theme.palette.common.white,
        boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
        mr: 0.25,
    },
};

export const filterButtonSx: SxProps<Theme> = {
    '&.MuiIconButton-root': {
        height: CONTROL_HEIGHT,
        px: 1.5,
        gap: 0.75,
        background: theme.palette.common.white,
        border: CONTROL_BORDER,
        borderRadius: CONTROL_RADIUS,
        color: theme.palette.neutral[500],
        '&:hover': {
            borderColor: theme.palette.neutral[300],
            background: theme.palette.common.white,
        },
        '& svg': {
            fontSize: '20px',
        },
    },
};

export const filterButtonTextSx: SxProps<Theme> = {
    fontSize: '14px',
    color: 'inherit',
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
};

export const filterButtonWrapperSx: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
};

export const filterIconActiveSx: SxProps<Theme> = {
    '&.MuiSvgIcon-root': {
        fill: theme.palette.primary.main,
    },
};

export const filterPopoverSx: SxProps<Theme> = {
    '& .MuiPaper-root': {
        maxWidth: '320px',
        width: '100%',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
        mt: 0.75,
    },
};

/**
 * A text field sized for the filter band.
 *
 * Same 2rem as every select beside it — the band's whole premise is that nothing in it is taller than
 * anything else, so a wrap only ever adds a line.
 */
export const filterInputSx: SxProps<Theme> = {
    width: '100%',
    height: '2rem',
    minHeight: '2rem',
    background: theme.palette.common.white,
    '& .MuiInputBase-input': {
        fontSize: theme.spacing(12 / 8),
        padding: '8px',
        // See the search box: under 16px, focusing the field zooms the whole page on iOS.
        [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
        },
    },
};
